import { zodResolver } from '@hookform/resolvers/zod'
import { CURRENCY } from '@idc-forms/schemas'
import {
  type V2CreateAdminProjectInput,
  V2CreateAdminProjectSchema
} from '@idc-forms/schemas/api-v2/admin-projects'
import { Controller, useForm } from 'react-hook-form'
import { useSocietiesV2LookupQuery } from '@/features/societies/hooks/use-societies-v2-lookup-query'
import { useUsersV2SelectionQuery } from '@/features/users/hooks/use-users-v2-selection-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { ImageDropzone, ImageSpec } from '@/shared/form/image-dropzone'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { MultiSelect } from '@/shared/ui/multi-select'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'

const CURRENCY_LABELS: Record<string, string> = {
  USD: 'USD — Dólares',
  GTQ: 'GTQ — Quetzales'
}

type ProjectV2FormProps = {
  /** Valores iniciales (edición) — ausente en creación. `society` es un `soc_`. */
  defaultValues?: Partial<V2CreateAdminProjectInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: V2CreateAdminProjectInput) => void
  onCancel: () => void
}

/**
 * Formulario de proyecto (crear/editar) para el side panel, contra la API v2.
 * Espeja el layout del form v1 pero cambia el transporte de las relaciones: la
 * sociedad se alimenta del lookup v2 de sociedades (valor `soc_`) y los
 * autorizadores del lookup v2 de usuarios (valor `usr_`). RHF + zodResolver con
 * el MISMO schema v2 que valida el backend, incluido el refine que exige ≥1
 * autorizador cuando `requiresAuthorization` está activo.
 */
export function ProjectV2Form({
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: ProjectV2FormProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<V2CreateAdminProjectInput>({
    resolver: zodResolver(V2CreateAdminProjectSchema),
    defaultValues: {
      currency: CURRENCY.GTQ,
      requiresAuthorization: false,
      authorizerIds: [],
      active: true,
      ...defaultValues
    }
  })

  const { data: societies = [] } = useSocietiesV2LookupQuery()
  const { data: users = [] } = useUsersV2SelectionQuery()
  const authorizerOptions = users.map((user) => ({ value: user.id, label: user.fullName }))

  const requiresAuthorization = watch('requiresAuthorization')

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="project-name">Nombre</FieldLabel>
                <Input
                  id="project-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.code)}>
                <FieldLabel htmlFor="project-code">Código</FieldLabel>
                <Input
                  id="project-code"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>
                  Único entre proyectos. Es el prefijo de los códigos de subtipos e inmuebles.
                </FieldDescription>
                <FieldError errors={[errors.code]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="society"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.society)}>
                <FieldLabel htmlFor="project-society">Sociedad</FieldLabel>
                <NativeSelect
                  id="project-society"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona una sociedad
                  </NativeSelectOption>
                  {societies.map((society) => (
                    <NativeSelectOption key={society.id} value={society.id}>
                      {society.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.society]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="currency"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.currency)}>
                <FieldLabel htmlFor="project-currency">Moneda</FieldLabel>
                <NativeSelect
                  id="project-currency"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  {Object.values(CURRENCY).map((value) => (
                    <NativeSelectOption key={value} value={value}>
                      {CURRENCY_LABELS[value]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                <FieldError errors={[errors.currency]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="image"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.image)}>
                <FieldLabel htmlFor="project-image">Logotipo para documentos</FieldLabel>
                <ImageDropzone
                  value={field.value ?? undefined}
                  onChange={(url) => {
                    field.onChange(url ?? undefined)
                  }}
                  placeholder="Arrastra una imagen del proyecto aquí o haz clic para seleccionar"
                  acceptedTypes={['image/jpeg', 'image/png', 'image/webp']}
                />
                {/*
                  Decía «Imagen» a secas, y desde que hay dos campos de marca eso ya no alcanza: sin
                  nombrar a cada uno por su destino, el asesor sube el mismo archivo a los dos.
                */}
                <FieldDescription>
                  El logotipo completo —escudo, nombre y tagline— tal como aparece en la cabecera de
                  los PDF generados.
                </FieldDescription>
                <FieldError errors={[errors.image]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="mark"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.mark)}>
                <FieldLabel htmlFor="project-mark">Logo para la aplicación</FieldLabel>
                <ImageDropzone
                  // ☠️ `inputId` propio. El dropzone tenía el id del `<input file>` clavado, y
                  // `getElementById` devuelve siempre el primero: con dos en el mismo formulario,
                  // clickear éste abría el selector del de arriba y el archivo se guardaba en el
                  // campo equivocado, sin error.
                  inputId="project-mark-input"
                  // La regla de 1024×1024 se valida acá antes de subir Y otra vez en el servidor.
                  spec={ImageSpec.projectMark}
                  value={field.value ?? undefined}
                  onChange={(url) => {
                    field.onChange(url ?? null)
                  }}
                  placeholder="Arrastra el logo cuadrado del proyecto o haz clic para seleccionar"
                  acceptedTypes={['image/png', 'image/webp']}
                />
                {/*
                  Se dice DÓNDE se ve y POR QUÉ es otro archivo: quien lo carga no abre la app, y sin
                  esta línea subiría el mismo logotipo de arriba y se vería ilegible en el teléfono.
                */}
                <FieldDescription>
                  Cuadrado de 1024×1024 píxeles, en PNG o WebP con fondo transparente. Es la marca
                  que el cliente ve sobre la foto de su inmueble en la aplicación, así que va sin el
                  nombre del desarrollo: a ese tamaño el texto no se lee.
                </FieldDescription>
                <FieldError errors={[errors.mark]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="address"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.address)}>
                <FieldLabel htmlFor="project-address">Dirección</FieldLabel>
                <Input
                  id="project-address"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="Zona 4 de Mixco, Guatemala"
                  autoComplete="off"
                />
                {/* Se dice a quién le llega, porque quien la escribe no ve la app: la
                    dirección aparece bajo el nombre del inmueble en el teléfono del
                    cliente, así que se escribe para que él la lea, no para archivo. */}
                <FieldDescription>
                  La ve el cliente bajo el nombre de su inmueble, en la aplicación. Una sola línea;
                  la comparten todos los inmuebles del proyecto.
                </FieldDescription>
                <FieldError errors={[errors.address]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Reservas</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="requiresAuthorization"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="project-requires-authorization">
                  Requiere autorización
                </FieldLabel>
                <Switch
                  id="project-requires-authorization"
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />

          {requiresAuthorization && (
            <Controller
              control={control}
              name="authorizerIds"
              render={({ field }) => (
                <Field data-invalid={Boolean(errors.authorizerIds)}>
                  <FieldLabel htmlFor="project-authorizers">Autorizadores</FieldLabel>
                  <MultiSelect
                    id="project-authorizers"
                    options={authorizerOptions}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    placeholder="Selecciona autorizadores"
                    searchPlaceholder="Buscar usuario..."
                    emptyText="Sin usuarios"
                  />
                  <FieldDescription>
                    Las reservas del proyecto esperarán la aprobación de estas personas.
                  </FieldDescription>
                  <FieldError errors={[errors.authorizerIds]} />
                </Field>
              )}
            />
          )}

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="project-active">Activo</FieldLabel>
                <Switch
                  id="project-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
